import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { closeFullScreenModalAction } from "../../../actions/modal";

import ButtonScogoClosedOutlined from "../../../common/Buttons/ButtonScogoClosedOutlined";
import ButtonScogoPrimary from "../../../common/Buttons/ButtonScogoPrimary";
import { postImageUrl } from "../../../actions/users";
import { bulkTicketCreate } from "../../../actions/service_request";
import { GET_TICKET_PROGRESS } from "../../../types/service_request";

const getErrorMessage = (error) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (typeof error === 'object') return error.message || error.error || JSON.stringify(error);
    return String(error);
};

export default function BulkFailed({ ticketProgress, type, name }) {

    const dispatch = useDispatch();

    const failedData = ticketProgress?.payload?.failed || [];
    const [data, setData] = useState(failedData);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    useEffect(() => {
        if (data.length === 1) {
            setSelectedRows([0]);
        }
    }, [data]);

    const handleChange = (index, key, value) => {
        setData(prevData => {
            const updatedData = [...prevData];
            updatedData[index][key] = value;
            return updatedData;
        });
    };

    const handleRowSelect = (index) => {
        setSelectedRows(prev => prev.includes(index)
            ? prev.filter(i => i !== index)
            : [...prev, index]
        );
    };

    const handleSelectAll = () => {
        setSelectAll(prev => !prev);
        setSelectedRows(prev => (prev.length === data.length ? [] : data.map((_, index) => index)));
    };

    function convertToCSV(arr) {
        const header = Object.keys(arr[0]).join(",");
        const rows = arr.map(obj => {
            return Object.values(obj).map(value => {
                // Escape double quotes and wrap in quotes if needed
                const val = value ? (typeof value === "object" ? getErrorMessage(value) : value.toString()) : "";
                if (val.includes(",") || val.includes('"') || val.includes("\n")) {
                    return `"${val.replace(/"/g, '""')}"`;
                }
                return val;
            }).join(",");
        });
        return [header, ...rows].join("\n");
    }

    const downloadCSV = () => {
        const csv = convertToCSV(data);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "data.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleSubmit = async () => {
        const selectedData = selectedRows.map(index => data[index]);
        if (selectedData.length === 0) {
            console.warn("No rows selected for submission.");
            return;
        }

        // Convert to CSV with snake_case headers
        const convertToSnakeCaseCSV = (data) => {
            if (data.length === 0) return "";

            // Convert headers to snake_case
            const headers = Object.keys(data[0])
                .filter(key => key !== "error")
                .map(header =>
                    header.toLowerCase()
                        .replace(/\s+/g, '_')
                        .replace(/-/g, '_')
                )
                .join(",");

            // Process rows
            const rows = data.map(row =>
                Object.keys(row)
                    .filter(key => key !== "error")
                    .map(key => `"${row[key]}"`)
                    .join(",")
            );

            return [headers, ...rows].join("\n");
        };

        const csvData = convertToSnakeCaseCSV(selectedData);
        const blob = new Blob([csvData], { type: "text/csv" });
        const file = new File([blob], "failed_tickets.csv", { type: "text/csv" });

        dispatch(
            postImageUrl({
                files: [file],
                ref: "bulk_failed",
                purpose: 'BULK',
                bulk_type: type,
                closeModal: true,
                onSuccess: (url) => dispatch(
                    bulkTicketCreate({
                        formName: "Bulk Failed Tickets",
                        type,
                        url,
                        org_id: ticketProgress.payload.org_id, // Replace with actual org_id
                        project_id: ticketProgress.payload.project_id // Replace with actual project_id
                    }))
            })
        );
        dispatch(closeFullScreenModalAction())
    };

    return (
        <div className="p-2 bg-gray-100 flex flex-col w-full h-full">
            <h1 className="text-font14 font-medium p-4">Failed {ticketProgress?.payload?.type}</h1>
            <div className="w-full bg-white p-4 rounded-lg shadow-md overflow-x-auto">
                <table className="w-full border-collapse font-normal text-font10 rounded-md bg-gray-50">
                    <thead>
                        <tr className="bg-gray-300 py-2 text-left">
                            <th className="p-1 border-b-2">
                                <input
                                    type="checkbox"
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    className="rounded-sm"
                                />
                            </th>
                            {data.length > 0 &&
                                Object.keys(data[0]).filter(key => key !== "error").map(key => (
                                    <th key={key} className="p-1 border-b-2">
                                        {key.split('_')
                                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                            .join(' ')}
                                    </th>
                                ))}
                            <th className="p-1 border-b-2">Error</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, rowIndex) => (
                            <tr key={rowIndex} className={`${rowIndex % 2 === 0 ? "bg-gray-50" : "bg-gray-70"} transition duration-300`}>
                                <td className="p-2 border-b-1 text-center">
                                    <input
                                        type="checkbox"
                                        className="rounded-sm"
                                        checked={selectedRows.includes(rowIndex)}
                                        onChange={() => handleRowSelect(rowIndex)}
                                    />
                                </td>
                                {Object.keys(row).filter(key => key !== "error").map(key => (
                                    <td key={key} className="p-2 border-b-1">
                                        <input
                                            type="text"
                                            value={row[key]}
                                            onChange={(e) => handleChange(rowIndex, key, e.target.value)}
                                            className="w-full p-1 border-none rounded text-font10 focus:outline-none focus:ring-2 focus:ring-blue-300"
                                        />
                                    </td>
                                ))}
                                <td className="p-2 text-red-500 text-font10 border-b-1">{getErrorMessage(row.error)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="flex p-3 gap-2">
                <ButtonScogoClosedOutlined textOrComponent='Cancel' type='button' onClick={() => {
                    dispatch(closeFullScreenModalAction())
                    dispatch({ type: GET_TICKET_PROGRESS, payload: {} })
                }} />
                <ButtonScogoPrimary textOrComponent='Submit' onClick={handleSubmit} />
                <ButtonScogoPrimary textOrComponent=' Download Sample File(CSV)' onClick={downloadCSV} />
            </div>
        </div>
    );
}
